// Importing necessary hooks, components, and icons
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useResponsibleJobs } from '../hooks/useResponsibleJobs'; // Custom hook for fetching jobs
import { useJobManagement } from '../hooks/useJobManagement'; // Custom hook for job status and deletion
import { useAuth } from '../hooks/useAuth'; // Custom hook for authentication
import { LoadingSpinner } from '../components/LoadingSpinner'; // Spinner component for loading states
import { DashboardHeader } from '../components/dashboard/DashboardHeader'; // Header component for the dashboard
import { JobsSection } from '../components/dashboard/JobsSection'; // Component for displaying job listings
import { CreateJobModal } from '../components/dashboard/CreateJobModal'; // Modal for creating new jobs

// Dashboard Component
export function Dashboard() {
  const { user } = useAuth(); // Get the authenticated user
  const { jobs, loading, error, refetch } = useResponsibleJobs(); // Fetch jobs related to the user
  const { toggleJobStatus, deleteJob } = useJobManagement(); // Actions to manage job status and deletion
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false); // State to manage the job creation modal

  // Handle success of job creation
  const handleCreateSuccess = async () => {
    setIsCreateModalOpen(false); // Close the modal
    await refetch(); // Refresh the job list immediately
  };

  // Handle toggling the status of a job
  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    if (window.confirm('Êtes-vous sûr de vouloir modifier le statut de cette offre ?')) {
      try {
        await toggleJobStatus(id, currentStatus); // Call the API to toggle status
        await refetch(); // Refresh the job list immediately
      } catch (error) {
        console.error('Error updating job status:', error); // Log errors
      }
    }
  };

  // Handle deleting a job
  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      try {
        await deleteJob(id); // Call the API to delete the job
        await refetch(); // Refresh the job list immediately
      } catch (error) {
        console.error('Error deleting job:', error); // Log errors
      }
    }
  };

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Show an error message if there was an issue fetching jobs
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center p-4 bg-accent-error text-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 mr-2 text-red-800" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Main dashboard layout
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header section */}
      <DashboardHeader 
        user={user} // Pass the authenticated user
        onCreateJob={() => setIsCreateModalOpen(true)} // Open job creation modal
      />

      {/* Jobs section */}
      <JobsSection
        jobs={jobs} // Pass the list of jobs
        onToggleStatus={handleToggleStatus} // Pass the toggle status handler
        onDelete={handleDelete} // Pass the delete handler
      />

      {/* Job creation modal */}
      {isCreateModalOpen && (
        <CreateJobModal
          onClose={() => setIsCreateModalOpen(false)} // Close the modal
          onSuccess={handleCreateSuccess} // Handle modal success
        />
      )}
    </div>
  );
}
